import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import {
  RefreshCw, StopCircle, Download, ExternalLink,
  X, RotateCcw,
  Users, AlertTriangle, Database, Archive,
  Loader2, CheckCircle2, XCircle, Clock, Link2, Unlink2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toaster, toast } from "@/components/ui/sonner";
import PageHeader from "@/components/PageHeader";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const API = `${window.location.origin}/api`;

/* ── tiny ding sound for dismiss ────────────────────────────── */
const playDing = () => {
  try {
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.connect(g); g.connect(ac.destination);
    o.frequency.value = 880; o.type = "sine";
    g.gain.value = 0.12;
    o.start(); g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.3);
    o.stop(ac.currentTime + 0.35);
  } catch (err) { console.debug("AudioContext beep:", err?.message); }
};

/* ════════════════════════════════════════════════════════════════
   STAT CARD
   ════════════════════════════════════════════════════════════════ */
function StatCard({ icon: Icon, label, value, color = "blue" }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    slate: "bg-slate-50 text-slate-600",
    green: "bg-green-50 text-green-600",
  };
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-800">{value ?? "–"}</p>
          <p className="text-xs text-slate-500 mt-0.5">{label}</p>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   MAIN MATCHING PAGE
   ════════════════════════════════════════════════════════════════ */
export default function MatchingPage() {
  const { user: _user } = useAuth();

  /* ── state ────────────────────────────────────────────────── */
  const [stats, setStats] = useState(null);
  const [syncLog, setSyncLog] = useState(null);
  const [syncRunning, setSyncRunning] = useState(false);
  const [tab, setTab] = useState("missing");

  // Data tables
  const [missing, setMissing] = useState({ contacts: [], total: 0 });
  const [mismatches, setMismatches] = useState({ mismatches: [], total: 0 });
  const [dismissed, setDismissed] = useState({ dismissed: [], total: 0 });
  const [ghlOnly, setGhlOnly] = useState({ contacts: [], total: 0 });
  const [_ghlNoCode, setGhlNoCode] = useState({ contacts: [], total: 0 });
  const [schedulerInfo, setSchedulerInfo] = useState(null);
  const [checkpoint, setCheckpoint] = useState(null); // { available, cached_contacts, ... }

  // UI
  const [_importing, setImporting] = useState(false);
  const [dismissingCode, setDismissingCode] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [refreshingGoogle, setRefreshingGoogle] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  /* ── fetch helpers ────────────────────────────────────────── */
  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sync/stats`, { withCredentials: true });
      setStats(data);
      setSyncRunning(data.sync_running);
    } catch (err) { console.error("fetchStats:", err); }
  }, []);

  const fetchSyncLog = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sync/logs?limit=1`, { withCredentials: true });
      if (data.logs?.length) setSyncLog(data.logs[0]);
      setSyncRunning(data.sync_running);
    } catch (err) { console.error("fetchSyncLog:", err); }
  }, []);

  const fetchMissing = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sync/missing`, { withCredentials: true });
      setMissing(data);
    } catch (err) { console.error("fetchMissing:", err); }
  }, []);

  const fetchMismatches = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sync/contact-mismatches`, { withCredentials: true });
      setMismatches(data);
    } catch (err) { console.error("fetchMismatches:", err); }
  }, []);

  const fetchDismissed = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sync/dismissed-mismatches`, { withCredentials: true });
      setDismissed(data);
    } catch (err) { console.error("fetchDismissed:", err); }
  }, []);

  const fetchGhlOnly = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sync/ghl-only`, { withCredentials: true });
      setGhlOnly(data);
    } catch (err) { console.error("fetchGhlOnly:", err); }
  }, []);

  const _fetchGhlNoCode = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sync/ghl-no-code`, { withCredentials: true });
      setGhlNoCode(data);
    } catch (err) { console.error("fetchGhlNoCode:", err); }
  }, []);

  const fetchSchedulerInfo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/scheduler/status`, { withCredentials: true });
      setSchedulerInfo(data);
    } catch (err) { console.error("fetchSchedulerInfo:", err); }
  }, []);

  const fetchGoogleStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/google-contacts/status`, { withCredentials: true });
      setGoogleStatus(data);
      return data;
    } catch (err) { console.error("fetchGoogleStatus:", err); return null; }
  }, []);

  const fetchCheckpoint = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sync/checkpoint`, { withCredentials: true });
      setCheckpoint(data);
    } catch (err) { console.error("fetchCheckpoint:", err); }
  }, []);

  /* ── initial load ─────────────────────────────────────────── */
  useEffect(() => {
    fetchStats();
    fetchSyncLog();
    fetchSchedulerInfo();
    fetchCheckpoint();
    fetchGoogleStatus().then((status) => {
      // Auto-refresh Google contacts if connected (silent mode)
      if (status?.connected) {
        refreshGoogle(true);
      }
    });

    // Handle Google OAuth callback params
    const params = new URLSearchParams(window.location.search);
    const authResult = params.get("google_auth");
    if (authResult === "success") {
      const count = params.get("count");
      toast.success(`Google Contacts connesso! ${count || ""} contatti importati`);
      fetchStats();
      window.history.replaceState({}, "", "/matching");
    } else if (authResult === "error") {
      toast.error("Errore nella connessione a Google Contacts");
      window.history.replaceState({}, "", "/matching");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStats, fetchSyncLog, fetchGoogleStatus, fetchSchedulerInfo]);

  /* ── polling during sync ──────────────────────────────────── */
  useEffect(() => {
    if (syncRunning) {
      pollRef.current = setInterval(() => {
        fetchSyncLog();
        fetchStats();
      }, 2000);
    } else {
      clearInterval(pollRef.current);
      // Al termine del sync, ricontrolla lo stato del checkpoint
      // (potrebbe essere stato azzerato dopo un successo, o creato
      // dopo un'interruzione).
      fetchCheckpoint();
    }
    return () => clearInterval(pollRef.current);
  }, [syncRunning, fetchSyncLog, fetchStats, fetchCheckpoint]);

  /* ── load tab data when switching ─────────────────────────── */
  useEffect(() => {
    if (tab === "missing") fetchMissing();
    else if (tab === "mismatches") { fetchMismatches(); fetchDismissed(); }
    else if (tab === "ghl-only") fetchGhlOnly();
  }, [tab, fetchMissing, fetchMismatches, fetchDismissed, fetchGhlOnly]);

  /* ── actions ──────────────────────────────────────────────── */
  const startSync = async () => {
    try {
      await axios.post(`${API}/sync/run`, {}, { withCredentials: true });
      setSyncRunning(true);
      toast.success("Sincronizzazione avviata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'avvio sync");
    }
  };

  const resumeSync = async () => {
    try {
      const { data } = await axios.post(`${API}/sync/resume`, {}, { withCredentials: true });
      setSyncRunning(true);
      toast.success(`Ripresa da checkpoint (${data.cached_contacts?.toLocaleString() || 0} contatti già scaricati)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella ripresa del sync");
    }
  };

  const stopSync = async () => {
    try {
      await axios.post(`${API}/sync/stop`, {}, { withCredentials: true });
      toast.info("Stop richiesto...");
    } catch (err) { console.error("stopSync:", err); }
  };

  const _handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/sync/import-google-contacts`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.imported} contatti Google importati`);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'importazione");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const connectGoogle = () => {
    window.location.href = `${API}/google-contacts/auth`;
  };

  const refreshGoogle = async (silent = false) => {
    setRefreshingGoogle(true);
    try {
      const { data } = await axios.post(`${API}/google-contacts/refresh`, {}, { withCredentials: true });
      if (!silent) toast.success(`${data.imported} contatti Google aggiornati`);
      fetchStats();
      fetchGoogleStatus();
    } catch (e) {
      if (e.response?.status === 401) {
        // Token revoked — show disconnected state
        toast.error("Token Google scaduto. Riconnetti Google Contacts.");
        fetchGoogleStatus();
      } else if (!silent) {
        toast.error(e.response?.data?.detail || "Errore nell'aggiornamento contatti Google");
      }
    } finally {
      setRefreshingGoogle(false);
    }
  };

  const exportMissingCSV = () => {
    window.open(`${API}/sync/missing/csv`, "_blank");
  };

  const dismissMismatch = async (code) => {
    setDismissingCode(code);
    try {
      await axios.post(`${API}/sync/contact-mismatches/${code}/dismiss`, {}, { withCredentials: true });
      playDing();
      setMismatches((prev) => ({
        ...prev,
        mismatches: prev.mismatches.filter((m) => m.qedbcode !== code),
        total: prev.total - 1,
      }));
      fetchStats();
      fetchDismissed();
    } catch {
      toast.error("Errore nell'ignorare l'incongruenza");
    } finally {
      setDismissingCode(null);
    }
  };

  const restoreDismissed = async (code) => {
    try {
      await axios.post(`${API}/sync/dismissed-mismatches/${code}/restore`, {}, { withCredentials: true });
      toast.success("Incongruenza ripristinata");
      fetchDismissed();
      fetchMismatches();
      fetchStats();
    } catch {
      toast.error("Errore nel ripristino");
    }
  };

  /* ── sync progress ────────────────────────────────────────── */
  const progressPct = syncLog ? Math.min(100, Math.round((syncLog.processed / Math.max(syncLog.total, 1)) * 100)) : 0;
  const etaMin = syncLog?.eta_seconds ? Math.ceil(syncLog.eta_seconds / 60) : 0;

  /* ════════════════════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-[#f5f7fa] bg-grid-light pt-[calc(7px+3.5rem+13px)]">
      <Toaster position="top-right" theme="light" />
      <div className="max-w-6xl mx-auto px-3 sm:px-6 pb-12">

        {/* ── HEADER ──────────────────────────────────────── */}
        <PageHeader
          title="Matching Contatti"
          subtitle="Sincronizza e confronta contatti Google con GoHighLevel"
        />

        {/* ── MAIN CONTENT CARD ───────────────────────────── */}
        <div className="bg-white rounded-3xl shadow-md -mt-8 relative z-10 p-4 sm:p-8">

          {/* ── STATS CARDS ───────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <StatCard icon={Users} label="Contatti Google" value={stats?.google_total ?? "–"} color="blue" />
            <StatCard icon={XCircle} label="Solo QE" value={stats?.missing ?? "–"} color="red" />
            <StatCard icon={Database} label="Solo GHL" value={stats?.ghl_only ?? "–"} color="slate" />
            <StatCard icon={AlertTriangle} label="Incongruenze" value={stats?.mismatches ?? "–"} color="amber" />
            <StatCard icon={Archive} label="Incongruenze Archiviate" value={stats?.dismissed ?? "–"} color="green" />
          </div>

          {/* ── IMPORT + SYNC CONTROLS ────────────────────── */}
          <div className="bg-slate-50 rounded-2xl p-4 sm:p-6 mb-6 space-y-4">
            {/* Google Contacts connection status */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap pb-3 border-b border-slate-200">
              {googleStatus?.connected ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Link2 className="w-4 h-4" />
                    <span className="font-medium">Google Contacts connesso</span>
                    {refreshingGoogle && (
                      <span className="flex items-center gap-1 text-blue-500">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Aggiornamento in corso...
                      </span>
                    )}
                    {!refreshingGoogle && googleStatus.last_update && (
                      <span className="text-slate-400 text-xs">
                        (aggiornato: {new Date(googleStatus.last_update).toLocaleString("it-IT")})
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Unlink2 className="w-4 h-4" />
                    <span>Google Contacts non connesso</span>
                  </div>
                  <Button
                    onClick={connectGoogle}
                    className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg shadow-sm"
                    size="sm"
                    data-testid="connect-google-btn"
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    Connetti Google Contacts
                  </Button>
                </>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
              {/* Last sync result */}
              {syncLog && syncLog.status !== "running" && (
                <div className={`flex items-center gap-2 text-sm ${syncLog.status === "completed" ? "text-green-600" : syncLog.status === "error" ? "text-red-500" : "text-slate-500"}`}>
                  {syncLog.status === "completed" ? <CheckCircle2 className="w-4 h-4" /> :
                   syncLog.status === "error" ? <XCircle className="w-4 h-4" /> :
                   <Clock className="w-4 h-4" />}
                  <span>{syncLog.message}</span>
                  {syncLog.finished_at && (
                    <span className="text-slate-400 ml-1">({new Date(syncLog.finished_at).toLocaleString("it-IT")})</span>
                  )}
                </div>
              )}

              <div className="flex-1" />

              {/* Sync buttons */}
              {!syncRunning ? (
                <>
                  {checkpoint?.available && (
                    <Button
                      onClick={resumeSync}
                      className="bg-amber-500 hover:bg-amber-400 text-white rounded-xl transition-colors"
                      title={`Riprendi dal checkpoint del ${checkpoint.checkpoint_at ? new Date(checkpoint.checkpoint_at).toLocaleString("it-IT") : "?"}`}
                      data-testid="resume-sync-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" /> Riprendi ({checkpoint.cached_contacts?.toLocaleString() || 0})
                    </Button>
                  )}
                  <Button
                    onClick={startSync}
                    className="bg-[#1a1fa8] hover:bg-[#f8dfd6] hover:text-[#1a1fa8] text-white rounded-xl transition-colors"
                    disabled={!stats || stats.google_total === 0}
                    data-testid="start-sync-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> {checkpoint?.available ? "Nuovo Sync" : "Avvia Sincronizzazione"}
                  </Button>
                </>
              ) : (
                <Button onClick={stopSync} variant="destructive" className="rounded-xl" data-testid="stop-sync-btn">
                  <StopCircle className="w-4 h-4 mr-2" /> Stop Sync
                </Button>
              )}
            </div>

            {/* Progress bar */}
            {syncLog && (syncRunning || syncLog.status === "running") && (
              <div className="space-y-2" data-testid="sync-progress">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 font-medium">{syncLog.message}</span>
                  <span className="text-slate-500">
                    {syncLog.processed?.toLocaleString()} / {syncLog.total?.toLocaleString()}
                    {etaMin > 0 && ` · ~${etaMin} min`}
                  </span>
                </div>
                <Progress value={progressPct} className="h-3" />
                <p className="text-xs text-slate-400 text-right">{progressPct}%</p>
              </div>
            )}

            {/* Incomplete-sync warning banner */}
            {syncLog && syncLog.status === "completed" && (syncLog.stats?.phase1_incomplete || syncLog.stats?.phase2_incomplete) && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-300 text-amber-900" data-testid="sync-incomplete-banner">
                <span className="text-lg leading-none pt-0.5">⚠</span>
                <div className="flex-1 text-sm">
                  <p className="font-semibold">Sincronizzazione incompleta</p>
                  <p className="text-xs mt-1 text-amber-800">
                    {syncLog.stats?.phase1_incomplete && (
                      <>Fase 1 (scaricamento GHL): {syncLog.stats.ghl_downloaded?.toLocaleString() ?? "?"} contatti scaricati prima dell&apos;interruzione. </>
                    )}
                    {syncLog.stats?.phase2_incomplete && <>Fase 2 (ricerca alfabetica) fermata per timeout. </>}
                    {checkpoint?.available
                      ? <>Usa <span className="font-semibold">Riprendi</span> per continuare dal checkpoint senza riscaricare i {checkpoint.cached_contacts?.toLocaleString() || 0} contatti già presenti.</>
                      : <>Riesegui il sync per completare i dati mancanti.</>
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Scheduler info */}
            {schedulerInfo && (
              <div className="flex items-center gap-2 text-xs text-slate-400 pt-2 border-t border-slate-200" data-testid="scheduler-info">
                <Clock className="w-3.5 h-3.5" />
                <span>
                  Automatismi: <span className="font-medium text-slate-500">{schedulerInfo.schedule || "Non attivi"}</span>
                  {schedulerInfo.jobs?.length > 0 && (
                    <> · Prossimo: <span className="font-medium text-slate-500">
                      {new Date(schedulerInfo.jobs[0].next_run).toLocaleString("it-IT")}
                    </span></>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* ── TABS ──────────────────────────────────────── */}
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="w-full justify-start bg-slate-100 rounded-xl p-1 mb-4 flex-wrap h-auto gap-1">
              <TabsTrigger value="missing" className="rounded-lg text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-missing">
                Solo QE {stats?.missing ? <Badge variant="destructive" className="ml-1.5 text-xs">{stats.missing}</Badge> : null}
              </TabsTrigger>
              <TabsTrigger value="ghl-only" className="rounded-lg text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-ghl-only">
                Solo GHL {stats?.ghl_only ? <Badge className="ml-1.5 text-xs bg-slate-500 text-white">{stats.ghl_only}</Badge> : null}
              </TabsTrigger>
              <TabsTrigger value="mismatches" className="rounded-lg text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-mismatches">
                Incongruenze {stats?.mismatches ? <Badge className="ml-1.5 text-xs bg-amber-500">{stats.mismatches}</Badge> : null}
              </TabsTrigger>
              <a
                href="https://api.bddstudiodentistico.it/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-white rounded-lg transition-colors"
                data-testid="resolve-contacts-btn"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Risolvi contatti
              </a>
            </TabsList>

            {/* ── TAB: Mancanti ───────────────────────────── */}
            <TabsContent value="missing">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-700">Contatti Solo QE ({missing.total})</h3>
                {missing.total > 0 && (
                  <Button variant="outline" size="sm" onClick={exportMissingCSV} className="rounded-lg" data-testid="export-csv-btn">
                    <Download className="w-4 h-4 mr-1" /> Esporta CSV
                  </Button>
                )}
              </div>
              {missing.contacts.length === 0 ? (
                <p className="text-sm text-slate-400 py-8 text-center">Nessun contatto mancante trovato. Esegui una sincronizzazione.</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>QEDBCODE</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Ruolo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {missing.contacts.map((c, i) => (
                        <TableRow key={c.qedbcode || i}>
                          <TableCell className="font-mono text-xs">{c.qedbcode}</TableCell>
                          <TableCell>{c.name}</TableCell>
                          <TableCell className="text-sm">{c.mobile}</TableCell>
                          <TableCell className="text-sm">{c.email}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{c.role || "–"}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* ── TAB: Incongruenze ───────────────────────── */}
            <TabsContent value="mismatches">
              <h3 className="font-semibold text-slate-700 mb-4">Incongruenze ({mismatches.total})</h3>
              {mismatches.mismatches.length === 0 ? (
                <p className="text-sm text-slate-400 py-8 text-center">Nessuna incongruenza trovata.</p>
              ) : (
                <div className="space-y-4 mb-8">
                  {mismatches.mismatches.map((m) => {
                    const diffSet = new Set((m.diff_fields || m.differences?.map(d => d.field)) || []);
                    const diffCount = diffSet.size;
                    return (
                      <div
                        key={m.qedbcode}
                        className={`rounded-xl border border-slate-200 overflow-hidden transition-all duration-300 ${dismissingCode === m.qedbcode ? "opacity-0 translate-x-4" : ""}`}
                        data-testid={`mismatch-card-${m.qedbcode}`}
                      >
                        {/* Card header */}
                        <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5">
                          <div className="flex items-center gap-3 flex-wrap">
                            <Badge className="bg-[#3B42DD] text-white text-xs font-mono">{m.qedbcode}</Badge>
                            <span className="font-medium text-slate-700 text-sm">{m.name}</span>
                            {m.ghl_role && <Badge variant="outline" className="text-xs">{m.ghl_role}</Badge>}
                            <span className="text-xs text-slate-400">{m.updated_at ? new Date(m.updated_at).toLocaleDateString("it-IT") : ""}</span>
                            <Badge className="bg-amber-100 text-amber-700 text-xs">{diffCount} diff</Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => dismissMismatch(m.qedbcode)}
                            className="h-7 w-7 text-slate-400 hover:text-red-500 hover:bg-red-50 shrink-0"
                            data-testid={`dismiss-${m.qedbcode}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Comparison grid */}
                        <div className="grid grid-cols-2 divide-x divide-slate-200">
                          {/* Google column */}
                          <div className="p-3">
                            <p className="text-xs font-semibold text-green-600 mb-2 uppercase tracking-wider">Google</p>
                            <div className="space-y-1.5">
                              <div className={`flex justify-between text-xs rounded px-2 py-1 ${diffSet.has("Mobile") ? "bg-green-50" : ""}`}>
                                <span className="text-slate-400">Mobile</span>
                                <span className={`font-medium ${diffSet.has("Mobile") ? "text-green-700" : "text-slate-600"}`}>{m.google?.mobile || "–"}</span>
                              </div>
                              <div className={`flex justify-between text-xs rounded px-2 py-1 ${diffSet.has("Home") ? "bg-green-50" : ""}`}>
                                <span className="text-slate-400">Home</span>
                                <span className={`font-medium ${diffSet.has("Home") ? "text-green-700" : "text-slate-600"}`}>{m.google?.home || "–"}</span>
                              </div>
                              <div className={`flex justify-between text-xs rounded px-2 py-1 ${diffSet.has("Email") ? "bg-green-50" : ""}`}>
                                <span className="text-slate-400">Email</span>
                                <span className={`font-medium ${diffSet.has("Email") ? "text-green-700" : "text-slate-600"}`}>{m.google?.email || "–"}</span>
                              </div>
                            </div>
                          </div>

                          {/* GHL column */}
                          <div className="p-3">
                            <p className="text-xs font-semibold text-red-500 mb-2 uppercase tracking-wider">GHL</p>
                            <div className="space-y-1.5">
                              <div className={`flex justify-between text-xs rounded px-2 py-1 ${diffSet.has("Mobile") ? "bg-red-50" : ""}`}>
                                <span className="text-slate-400">Phone</span>
                                <span className={`font-medium ${diffSet.has("Mobile") ? "text-red-600" : "text-slate-600"}`}>{m.ghl?.phone || "–"}</span>
                              </div>
                              <div className={`flex justify-between text-xs rounded px-2 py-1 ${diffSet.has("Home") ? "bg-red-50" : ""}`}>
                                <span className="text-slate-400">Ref F</span>
                                <span className={`font-medium ${diffSet.has("Home") ? "text-red-600" : "text-slate-600"}`}>{m.ghl?.ref_f || "–"}</span>
                              </div>
                              <div className={`flex justify-between text-xs rounded px-2 py-1 ${diffSet.has("Email") ? "bg-red-50" : ""}`}>
                                <span className="text-slate-400">Email</span>
                                <span className={`font-medium ${diffSet.has("Email") ? "text-red-600" : "text-slate-600"}`}>{m.ghl?.email || "–"}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Archivio ignorate */}
              <details className="group">
                <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-700 mb-3">
                  <Archive className="w-4 h-4" />
                  Archivio ignorate ({dismissed.total})
                </summary>
                {dismissed.dismissed.length === 0 ? (
                  <p className="text-sm text-slate-400 py-4 text-center">Archivio vuoto.</p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>QEDBCODE</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Campi</TableHead>
                          <TableHead>Rilevata</TableHead>
                          <TableHead>Ignorata</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dismissed.dismissed.map((d) => (
                          <TableRow key={d.qedbcode}>
                            <TableCell className="font-mono text-xs">{d.qedbcode}</TableCell>
                            <TableCell>{d.name}</TableCell>
                            <TableCell>
                              {d.differences?.map((df, i) => (
                                <Badge key={i} className="mr-1 text-xs bg-slate-100 text-slate-600">{df.field}</Badge>
                              ))}
                            </TableCell>
                            <TableCell className="text-xs text-slate-400">{d.detected_at ? new Date(d.detected_at).toLocaleDateString("it-IT") : "–"}</TableCell>
                            <TableCell className="text-xs text-slate-400">{d.dismissed_at ? new Date(d.dismissed_at).toLocaleDateString("it-IT") : "–"}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => restoreDismissed(d.qedbcode)}
                                className="h-8 w-8 text-slate-400 hover:text-blue-500 hover:bg-blue-50"
                                data-testid={`restore-${d.qedbcode}`}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </details>
            </TabsContent>

            {/* ── TAB: Solo GHL ───────────────────────────── */}
            <TabsContent value="ghl-only">
              <h3 className="font-semibold text-slate-700 mb-4">Contatti solo in GHL ({ghlOnly.total})</h3>
              {ghlOnly.contacts.length === 0 ? (
                <p className="text-sm text-slate-400 py-8 text-center">Nessun contatto solo GHL trovato.</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>QEDBCODE</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Telefono</TableHead>
                        <TableHead>Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ghlOnly.contacts.map((c, i) => (
                        <TableRow key={c.qedbcode || c.ghl_id || i}>
                          <TableCell className="font-mono text-xs">{c.qedbcode || <span className="text-orange-400 font-semibold">Mancante</span>}</TableCell>
                          <TableCell>{c.name || <span className="text-slate-300">Senza nome</span>}</TableCell>
                          <TableCell className="text-sm">{c.phone || "–"}</TableCell>
                          <TableCell className="text-sm">{c.email || "–"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

          </Tabs>

        </div>
      </div>
    </div>
  );
}
